import React, { useEffect, useState, useRef } from 'react';
import {
  ShieldCheck, Package, FileSearch, ClipboardList, Sparkles, Upload, Download,
  AlertTriangle, CheckCircle2, Clock, Send, Loader2, FileText, X, Trash2, Plus, Bot, FileDown,
  Hash, BadgeCheck, Percent, Receipt, FileSpreadsheet, ShoppingBag, TrendingUp, AlertOctagon, Inbox,
  ChevronDown, Scale, Info, Gauge,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  runInspectionAudit, downloadAuditPdf, generateInspectionPack, listInspectionPacks,
  listInspectionRequirements, createInspectionRequirement, updateInspectionRequirement,
  deleteInspectionRequirement, aiAnalyzeRequirement,
} from '../services/inspeccionApi';
import { StatCard, SurfaceCard, FiltersCard } from '../components/ui/filters-toolkit';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtBytes = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

const ORGANISMS = [
  { v: 'AEAT', label: 'AEAT · Hacienda estatal' },
  { v: 'foral', label: 'Hacienda Foral (País Vasco / Navarra)' },
  { v: 'ss', label: 'Seguridad Social' },
  { v: 'trabajo', label: 'Inspección de Trabajo' },
  { v: 'ccaa', label: 'Comunidad Autónoma' },
  { v: 'otros', label: 'Otros organismos' },
];

const SEV_STYLES = {
  critical: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  ok: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};
const SEV_ICONS = { critical: AlertTriangle, warning: AlertTriangle, ok: CheckCircle2 };

const REQ_STATUS = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'En curso', color: 'bg-sky-100 text-sky-700' },
  answered: { label: 'Respondido', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Cerrado', color: 'bg-stone-200 text-stone-600' },
};

// =========================================================================
// TAB: ASISTENTE IA
// =========================================================================
const AiAgentTab = ({ onPackOptionsReady }) => {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const analyze = async () => {
    if (!text.trim() && !file) {
      toast.error('Pega el texto del requerimiento o sube un archivo');
      return;
    }
    setBusy(true);
    try {
      const { data } = await aiAnalyzeRequirement(text, file);
      setResult(data);
      if (data?.parsed) toast.success('Requerimiento analizado');
      else toast.warning('La IA respondió pero no estructuró bien la salida');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error analizando requerimiento');
    } finally {
      setBusy(false);
    }
  };

  const applyPackOptions = () => {
    if (result?.parsed?.pack_options) {
      onPackOptionsReady(result.parsed.pack_options);
      toast.success('Configuración aplicada al generador. Cambia a la pestaña "Caja de Inspección"');
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 rounded-3xl p-6 text-white shadow-xl shadow-violet-200/40">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Bot className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-xl font-extrabold font-heading tracking-tight">Agente IA de Inspección</h2>
            <p className="text-xs text-white/80">Sube el requerimiento (texto, PDF o imagen) y la IA te lo procesa</p>
          </div>
        </div>
      </div>

      <SurfaceCard testid="ai-analyze-card">
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Texto del requerimiento (opcional)</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder="Pega aquí el texto del requerimiento. Ej: 'En relación al expediente 2025/EXP12345 se requiere aportar libros de IVA repercutido y soportado del ejercicio 2024…'"
              disabled={busy}
              className="mt-2 w-full resize-none rounded-2xl border border-stone-200 bg-stone-50 focus:bg-white focus:border-violet-300 focus:ring-2 focus:ring-violet-100 focus:outline-none p-3 text-sm transition-all"
              data-testid="ai-req-text"
            />
          </label>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-violet-200 text-violet-700 hover:bg-violet-50 text-sm font-bold"
              data-testid="ai-req-upload"
            >
              <Upload className="w-4 h-4" />
              {file ? file.name : 'Subir PDF / imagen del requerimiento'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file && (
              <button onClick={() => setFile(null)} className="text-xs text-stone-500 hover:text-red-600">
                <X className="w-3.5 h-3.5 inline" /> Quitar
              </button>
            )}

            <Button
              onClick={analyze}
              disabled={busy || (!text.trim() && !file)}
              className="ml-auto rounded-full bg-gradient-to-r from-violet-500 to-pink-500 hover:opacity-90 text-white"
              data-testid="ai-req-analyze"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {busy ? 'Analizando…' : 'Analizar con IA'}
            </Button>
          </div>
        </div>
      </SurfaceCard>

      {result?.parsed && (
        <SurfaceCard testid="ai-result-card">
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <h3 className="text-base font-extrabold font-heading">Análisis de la IA</h3>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-stone-50 rounded-xl p-3">
                <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500">Organismo</p>
                <p className="font-bold">{result.parsed.organism || '—'}</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-3">
                <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500">Expediente</p>
                <p className="font-bold">{result.parsed.reference || '—'}</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-3">
                <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500">Periodo</p>
                <p className="font-bold">{result.parsed.period_start} → {result.parsed.period_end}</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-3">
                <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500">Plazo</p>
                <p className="font-bold">{result.parsed.deadline || '—'}</p>
              </div>
            </div>

            {result.parsed.subject && (
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500 mb-1">Asunto</p>
                <p className="text-sm font-medium">{result.parsed.subject}</p>
              </div>
            )}

            {result.parsed.documents_required?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500 mb-2">Documentos solicitados</p>
                <div className="flex flex-wrap gap-1.5">
                  {result.parsed.documents_required.map((d, i) => (
                    <span key={i} className="text-xs font-bold bg-violet-100 text-violet-700 px-2 py-1 rounded-full">{d}</span>
                  ))}
                </div>
              </div>
            )}

            {result.parsed.summary && (
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
                <p className="text-[10px] uppercase font-bold tracking-widest text-violet-700 mb-1">Resumen ejecutivo</p>
                <p className="text-sm text-stone-700">{result.parsed.summary}</p>
              </div>
            )}

            {result.parsed.proposed_response && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-[10px] uppercase font-bold tracking-widest text-emerald-700 mb-1">Borrador de respuesta</p>
                <p className="text-sm text-stone-700 whitespace-pre-line">{result.parsed.proposed_response}</p>
              </div>
            )}

            {result.parsed.pack_options && (
              <Button
                onClick={applyPackOptions}
                className="w-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500 hover:opacity-90 text-white"
                data-testid="ai-apply-pack-options"
              >
                <Package className="w-4 h-4 mr-2" />
                Pre-cargar caja de inspección con estos parámetros
              </Button>
            )}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
};

// =========================================================================
// TAB: CAJA DE INSPECCIÓN
// =========================================================================
const PackTab = ({ initialOpts }) => {
  const [opts, setOpts] = useState({
    period_start: `${new Date().getFullYear()}-01-01`,
    period_end: `${new Date().getFullYear()}-12-31`,
    organism: 'AEAT',
    include_invoices_xlsx: true,
    include_expenses_xlsx: true,
    include_vat_books: true,
    include_invoices_pdf: true,
    note: '',
  });
  const [busy, setBusy] = useState(false);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    listInspectionPacks().then((r) => setPacks(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (initialOpts) setOpts((o) => ({ ...o, ...initialOpts }));
  }, [initialOpts]);

  const set = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  const generate = async () => {
    setBusy(true);
    try {
      const res = await generateInspectionPack(opts);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inspeccion_${opts.organism}_${opts.period_start}_${opts.period_end}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Paquete descargado');
      // refresh history
      const { data } = await listInspectionPacks();
      setPacks(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error generando paquete');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <SurfaceCard testid="pack-config-card">
        <div className="p-5 space-y-4">
          <h3 className="text-lg font-extrabold font-heading tracking-tight flex items-center gap-2">
            <Package className="w-5 h-5 text-violet-500" /> Configurar caja de inspección
          </h3>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Desde</span>
              <Input type="date" value={opts.period_start} onChange={(e) => set('period_start', e.target.value)} className="mt-1 h-10 rounded-xl" data-testid="pack-period-start" />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Hasta</span>
              <Input type="date" value={opts.period_end} onChange={(e) => set('period_end', e.target.value)} className="mt-1 h-10 rounded-xl" data-testid="pack-period-end" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Organismo solicitante</span>
              <Select value={opts.organism} onValueChange={(v) => set('organism', v)}>
                <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="pack-organism"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ORGANISMS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          </div>

          <div className="bg-stone-50 rounded-xl p-3 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-stone-500">Contenido del paquete</p>
            {[
              ['include_vat_books', 'Libros de IVA (repercutido + soportado)'],
              ['include_invoices_xlsx', 'CSV de facturas emitidas'],
              ['include_expenses_xlsx', 'CSV de gastos / facturas recibidas'],
              ['include_invoices_pdf', 'PDFs de cada factura emitida'],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <Checkbox checked={opts[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`pack-opt-${k}`} />
                <span>{l}</span>
              </label>
            ))}
          </div>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Nota interna (opcional)</span>
            <Input value={opts.note} onChange={(e) => set('note', e.target.value)} placeholder="Ej: Para responder al expediente 2025/EXP12345" className="mt-1 h-10 rounded-xl" data-testid="pack-note" />
          </label>

          <Button
            onClick={generate}
            disabled={busy}
            className="w-full rounded-full bg-[#FF6B4A] hover:bg-[#E05A3E] text-white shadow-lg shadow-orange-200/50 h-11"
            data-testid="pack-generate-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {busy ? 'Generando ZIP…' : 'Generar y descargar paquete'}
          </Button>
        </div>
      </SurfaceCard>

      {packs.length > 0 && (
        <SurfaceCard testid="pack-history-card">
          <div className="p-5">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-stone-500 mb-3">Historial de paquetes</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-stone-400 border-b border-stone-100">
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Organismo</th>
                    <th className="py-2">Periodo</th>
                    <th className="py-2 text-right">Tamaño</th>
                    <th className="py-2 text-right">Facturas</th>
                    <th className="py-2 text-right">Gastos</th>
                  </tr>
                </thead>
                <tbody>
                  {packs.map((p) => (
                    <tr key={p.id} className="border-b border-stone-50 hover:bg-stone-50" data-testid={`pack-row-${p.id}`}>
                      <td className="py-2">{(p.created_at || '').slice(0, 10)}</td>
                      <td className="py-2 font-bold">{p.organism}</td>
                      <td className="py-2 text-stone-500">{p.period_start} → {p.period_end}</td>
                      <td className="py-2 text-right font-mono text-xs">{fmtBytes(p.size_bytes || 0)}</td>
                      <td className="py-2 text-right">{p.invoices_count}</td>
                      <td className="py-2 text-right">{p.expenses_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SurfaceCard>
      )}
    </div>
  );
};

// =========================================================================
// TAB: AUDITORÍA EXHAUSTIVA TIPO HACIENDA
// =========================================================================
const CAT_ICONS = {
  Hash, BadgeCheck, Percent, Receipt, FileSpreadsheet, ShoppingBag, TrendingUp,
  AlertOctagon, Inbox, ShieldCheck,
};

const SEV_BADGE = {
  critical: 'bg-rose-100 text-rose-700 border-rose-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  info: 'bg-sky-100 text-sky-700 border-sky-200',
  ok: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};
const SEV_LABEL = { critical: 'CRÍTICO', warning: 'AVISO', info: 'INFO', ok: 'OK' };
const SEV_ICON = { critical: AlertTriangle, warning: AlertTriangle, info: Info, ok: CheckCircle2 };

const RISK_STYLES = {
  low: { label: 'Riesgo bajo', color: 'from-emerald-500 to-teal-500', text: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  medium: { label: 'Riesgo medio', color: 'from-amber-400 to-orange-500', text: 'text-amber-700', bg: 'bg-amber-50', ring: 'ring-amber-200' },
  high: { label: 'Riesgo alto', color: 'from-orange-500 to-rose-500', text: 'text-rose-600', bg: 'bg-rose-50', ring: 'ring-rose-200' },
  critical: { label: 'Riesgo crítico', color: 'from-rose-600 to-red-700', text: 'text-red-700', bg: 'bg-red-50', ring: 'ring-red-300' },
};

const ScoreGauge = ({ score, risk }) => {
  const r = RISK_STYLES[risk] || RISK_STYLES.medium;
  const circumference = 2 * Math.PI * 56;
  const offset = circumference * (1 - score / 100);
  return (
    <div className="relative w-44 h-44 flex items-center justify-center" data-testid="audit-score-gauge">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 128 128">
        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="10" fill="none" className="text-stone-100" />
        <circle
          cx="64" cy="64" r="56" strokeWidth="10" fill="none" strokeLinecap="round"
          stroke="url(#scoreGrad)" strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)' }}
        />
        <defs>
          <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#e11d48" />
          </linearGradient>
        </defs>
      </svg>
      <div className="text-center">
        <div className={`text-5xl font-extrabold font-heading tracking-tight ${r.text}`}>{score}</div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mt-1">/ 100</div>
      </div>
    </div>
  );
};

const CheckCard = ({ check }) => {
  const [open, setOpen] = useState(false);
  const Icon = SEV_ICON[check.severity] || CheckCircle2;
  const isCritical = check.severity === 'critical' && !check.passed;
  const isWarn = check.severity === 'warning' && !check.passed;
  const bgClass = check.passed
    ? 'bg-emerald-50/40 border-emerald-100'
    : isCritical
      ? 'bg-rose-50/60 border-rose-200'
      : isWarn
        ? 'bg-amber-50/60 border-amber-200'
        : 'bg-sky-50/40 border-sky-100';
  const iconClass = check.passed
    ? 'text-emerald-500'
    : isCritical
      ? 'text-rose-500'
      : isWarn
        ? 'text-amber-500'
        : 'text-sky-500';

  const hasDetails = check.items?.length > 0 || check.recommendation || check.law;

  return (
    <div
      className={`rounded-2xl border ${bgClass} transition-all`}
      data-testid={`audit-check-${check.id}`}
    >
      <button
        type="button"
        onClick={() => hasDetails && setOpen((o) => !o)}
        disabled={!hasDetails}
        className="w-full text-left p-4 flex items-start gap-3 group"
      >
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconClass}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm text-stone-800">{check.title}</span>
            <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded border ${SEV_BADGE[check.severity]}`}>
              {SEV_LABEL[check.severity]}
            </span>
          </div>
          <p className="text-xs text-stone-600 mt-1">{check.detail}</p>
        </div>
        {hasDetails && (
          <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform mt-1 flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && hasDetails && (
        <div className="px-4 pb-4 pl-12 space-y-3 text-xs">
          {check.recommendation && (
            <div className="bg-white/70 backdrop-blur rounded-xl p-3 border border-stone-100">
              <p className="text-[10px] uppercase font-bold tracking-widest text-violet-600 mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Recomendación
              </p>
              <p className="text-stone-700 leading-relaxed">{check.recommendation}</p>
            </div>
          )}
          {check.law && (
            <div className="flex items-start gap-2 text-stone-500">
              <Scale className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <p className="italic">{check.law}</p>
            </div>
          )}
          {check.items?.length > 0 && (
            <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
              <div className="px-3 py-2 bg-stone-50 border-b border-stone-100 flex items-center justify-between">
                <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500">
                  Detalle ({check.items_total > check.items.length ? `${check.items.length} de ${check.items_total}` : check.items.length})
                </p>
              </div>
              <ul className="divide-y divide-stone-50 max-h-64 overflow-y-auto">
                {check.items.map((it, i) => (
                  <li key={i} className="px-3 py-2 font-mono text-[11px] text-stone-700">
                    {typeof it === 'object' ? Object.entries(it).map(([k, v]) => (
                      <span key={k} className="mr-3"><span className="text-stone-400">{k}:</span> {String(v)}</span>
                    )) : String(it)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CategoryCard = ({ category, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = CAT_ICONS[category.icon] || ShieldCheck;
  const s = category.summary || {};
  const hasIssues = s.critical > 0 || s.warning > 0;
  return (
    <SurfaceCard testid={`audit-cat-${category.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full p-5 flex items-center gap-4 group"
      >
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${hasIssues ? 'bg-gradient-to-br from-rose-500 to-orange-500' : 'bg-gradient-to-br from-emerald-500 to-teal-500'}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-base font-extrabold font-heading tracking-tight text-stone-800">{category.title}</p>
          <p className="text-xs text-stone-500 mt-0.5">
            {s.total} check{s.total === 1 ? '' : 's'} ·{' '}
            <span className="text-emerald-600 font-bold">{s.passed} OK</span>
            {s.warning > 0 && <> · <span className="text-amber-600 font-bold">{s.warning} aviso{s.warning === 1 ? '' : 's'}</span></>}
            {s.critical > 0 && <> · <span className="text-rose-600 font-bold">{s.critical} crítico{s.critical === 1 ? '' : 's'}</span></>}
          </p>
        </div>
        <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-2 border-t border-stone-100 pt-4">
          {category.checks.map((c) => (
            <CheckCard key={c.id} check={c} />
          ))}
        </div>
      )}
    </SurfaceCard>
  );
};

const AuditTab = () => {
  const [busy, setBusy] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await runInspectionAudit();
      setResult(data);
    } catch (e) {
      toast.error('Error ejecutando auditoría');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await downloadAuditPdf();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria_fiscal_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Informe descargado');
    } catch (e) {
      toast.error('No se pudo generar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (busy && !result) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="audit-loading">
        <Loader2 className="w-10 h-10 animate-spin text-violet-500 mb-3" />
        <p className="text-sm font-bold text-stone-500">Ejecutando inspección integral…</p>
        <p className="text-xs text-stone-400 mt-1">Analizando facturas, gastos, NIFs, IVA, cadena Verifactu…</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="text-center py-12">
        <Button onClick={run} className="rounded-full bg-[#FF6B4A] hover:bg-[#E05A3E] text-white" data-testid="audit-run-btn">
          <FileSearch className="w-4 h-4 mr-2" /> Ejecutar auditoría
        </Button>
      </div>
    );
  }

  const { summary, categories, top_issues, data_scope, generated_at } = result;
  const risk = RISK_STYLES[summary.risk_level] || RISK_STYLES.medium;

  return (
    <div className="space-y-5">
      {/* HERO SCORE */}
      <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${risk.color} text-white shadow-xl p-6`} data-testid="audit-hero">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative flex flex-col md:flex-row items-center md:items-stretch gap-6">
          <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-4 flex items-center justify-center">
            <ScoreGauge score={summary.score} risk={summary.risk_level} />
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-2">
              <Gauge className="w-5 h-5" />
              <span className="text-xs font-extrabold uppercase tracking-widest opacity-90">Diagnóstico fiscal</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-extrabold mb-1">{risk.label}</h2>
            <p className="text-sm opacity-90 max-w-md mb-4">
              {summary.score >= 85 && 'Tu negocio está bien preparado para una inspección. Mantén las buenas prácticas.'}
              {summary.score >= 65 && summary.score < 85 && 'Tienes margen de mejora. Resuelve los avisos para reducir el riesgo.'}
              {summary.score >= 40 && summary.score < 65 && 'Hay problemas serios que deberías abordar antes de cualquier requerimiento.'}
              {summary.score < 40 && 'Riesgo elevado. Soluciona los puntos críticos cuanto antes — pueden ser sanciones.'}
            </p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-white/15 backdrop-blur rounded-xl py-2">
                <p className="text-2xl font-extrabold">{summary.critical}</p>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">Críticos</p>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-xl py-2">
                <p className="text-2xl font-extrabold">{summary.warning}</p>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">Avisos</p>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-xl py-2">
                <p className="text-2xl font-extrabold">{summary.info}</p>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">Info</p>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-xl py-2">
                <p className="text-2xl font-extrabold">{summary.passed}</p>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">Correctos</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SCOPE + RERUN */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-stone-500">
          Analizadas <span className="font-bold text-stone-700">{data_scope?.invoices || 0}</span> facturas,{' '}
          <span className="font-bold text-stone-700">{data_scope?.expenses || 0}</span> gastos,{' '}
          <span className="font-bold text-stone-700">{data_scope?.clients || 0}</span> clientes ·{' '}
          {new Date(generated_at).toLocaleString('es-ES')}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={downloadPdf}
            disabled={downloadingPdf}
            className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white shadow-md shadow-violet-200/50"
            data-testid="audit-pdf-btn"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            {downloadingPdf ? 'Generando…' : 'Descargar informe PDF'}
          </Button>
          <Button onClick={run} disabled={busy} variant="outline" className="rounded-full" data-testid="audit-run-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
            Volver a ejecutar
          </Button>
        </div>
      </div>

      {/* TOP ISSUES */}
      {top_issues?.length > 0 && (
        <SurfaceCard testid="audit-top-issues">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertOctagon className="w-5 h-5 text-rose-500" />
              <h3 className="text-base font-extrabold font-heading tracking-tight">Top {top_issues.length} puntos a resolver</h3>
            </div>
            <div className="space-y-2">
              {top_issues.map((c, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors" data-testid={`audit-top-${c.id}`}>
                  <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded border flex-shrink-0 mt-0.5 ${SEV_BADGE[c.severity]}`}>
                    {SEV_LABEL[c.severity]}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-stone-800">{c.title}</p>
                    <p className="text-xs text-stone-500 mt-0.5">{c.detail}</p>
                    <p className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mt-1">{c.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SurfaceCard>
      )}

      {/* CATEGORIES */}
      <div className="space-y-3">
        <h3 className="text-xs font-extrabold uppercase tracking-widest text-stone-500 pt-2">Áreas auditadas</h3>
        {categories.map((cat, i) => (
          <CategoryCard key={cat.id} category={cat} defaultOpen={i === 0 && (cat.summary?.critical > 0 || cat.summary?.warning > 0)} />
        ))}
      </div>
    </div>
  );
};

// =========================================================================
// TAB: REQUERIMIENTOS
// =========================================================================
const emptyReq = { organism: 'AEAT', reference: '', subject: '', received_date: new Date().toISOString().slice(0, 10), deadline: '', status: 'pending', notes: '' };

const RequirementsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyReq);

  const load = async () => {
    try { const { data } = await listInspectionRequirements(); setRows(data || []); }
    catch { toast.error('Error cargando requerimientos'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyReq); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyReq, ...r, deadline: r.deadline || '' }); setShowForm(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.deadline) delete payload.deadline;
      if (editing) await updateInspectionRequirement(editing.id, payload);
      else await createInspectionRequirement(payload);
      toast.success('Requerimiento guardado');
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este requerimiento?')) return;
    try { await deleteInspectionRequirement(id); toast.success('Eliminado'); load(); }
    catch { toast.error('Error eliminando'); }
  };

  const changeStatus = async (r, s) => {
    try { await updateInspectionRequirement(r.id, { status: s }); load(); }
    catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-extrabold uppercase tracking-widest text-stone-500">{rows.length} requerimiento{rows.length === 1 ? '' : 's'}</h3>
        <Button onClick={openCreate} className="rounded-full bg-[#FF6B4A] hover:bg-[#E05A3E] text-white" data-testid="req-create-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo requerimiento
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : rows.length === 0 ? (
        <SurfaceCard><div className="text-center py-12 text-stone-400">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 text-stone-300" />
          <p className="font-bold text-stone-600">Sin requerimientos registrados</p>
          <p className="text-xs mt-1">Cuando recibas un requerimiento de Hacienda u otro organismo, regístralo aquí.</p>
        </div></SurfaceCard>
      ) : (
        <SurfaceCard>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-stone-400 border-b border-stone-100">
                <th className="px-4 py-2">Organismo</th>
                <th className="px-4 py-2">Expediente</th>
                <th className="px-4 py-2">Asunto</th>
                <th className="px-4 py-2">Recibido</th>
                <th className="px-4 py-2">Plazo</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-stone-50 hover:bg-stone-50" data-testid={`req-row-${r.id}`}>
                  <td className="px-4 py-2 font-bold uppercase text-xs">{r.organism}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.reference || '—'}</td>
                  <td className="px-4 py-2 max-w-md truncate">{r.subject}</td>
                  <td className="px-4 py-2 text-xs text-stone-500">{r.received_date}</td>
                  <td className="px-4 py-2 text-xs">{r.deadline || '—'}</td>
                  <td className="px-4 py-2">
                    <Select value={r.status} onValueChange={(v) => changeStatus(r, v)}>
                      <SelectTrigger className={`h-7 w-auto border-0 px-2 rounded-full text-xs font-bold focus:ring-0 ${REQ_STATUS[r.status]?.color || ''}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(REQ_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => openEdit(r)} className="text-xs text-violet-700 hover:underline mr-3">Editar</button>
                    <button onClick={() => remove(r.id)} className="text-xs text-red-600 hover:underline" data-testid={`req-delete-${r.id}`}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SurfaceCard>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onMouseDown={() => setShowForm(false)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-stone-100 flex items-center justify-between">
              <h3 className="text-lg font-extrabold font-heading">{editing ? 'Editar requerimiento' : 'Nuevo requerimiento'}</h3>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-stone-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-stone-500">Organismo</span>
                  <Select value={form.organism} onValueChange={(v) => setForm({ ...form, organism: v })}>
                    <SelectTrigger className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{ORGANISMS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-stone-500">Nº Expediente</span>
                  <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 h-10 rounded-xl" />
                </label>
                <label className="block col-span-2"><span className="text-xs font-bold uppercase tracking-widest text-stone-500">Asunto *</span>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="req-subject" />
                </label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-stone-500">Recibido</span>
                  <Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} className="mt-1 h-10 rounded-xl" />
                </label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-stone-500">Plazo</span>
                  <Input type="date" value={form.deadline || ''} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1 h-10 rounded-xl" />
                </label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-stone-500">Notas</span>
                <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 w-full resize-none rounded-xl border border-stone-200 bg-stone-50 focus:bg-white p-3 text-sm" />
              </label>
            </div>
            <div className="p-5 border-t border-stone-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-full">Cancelar</Button>
              <Button onClick={save} disabled={!form.subject.trim()} className="rounded-full bg-[#FF6B4A] hover:bg-[#E05A3E] text-white" data-testid="req-save">Guardar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// PAGE
// =========================================================================
const TABS = [
  { k: 'ai', l: 'Agente IA', icon: Bot },
  { k: 'pack', l: 'Caja de Inspección', icon: Package },
  { k: 'audit', l: 'Auditoría Preventiva', icon: ShieldCheck },
  { k: 'req', l: 'Requerimientos', icon: ClipboardList },
];

export const InspeccionPage = () => {
  const [tab, setTab] = useState('ai');
  const [packPreset, setPackPreset] = useState(null);

  const handlePackOptionsReady = (opts) => {
    setPackPreset(opts);
    setTab('pack');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Agente de Inspección</h1>
        <p className="text-stone-500 text-sm mt-1">Responde a inspecciones de Hacienda, Seguridad Social, Trabajo o CCAA con la ayuda de la IA.</p>
      </div>

      <div className="flex gap-1 bg-white rounded-2xl border border-stone-100 shadow-sm p-1 w-fit flex-wrap" data-testid="inspeccion-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${tab === t.k ? 'bg-[#FF6B4A] text-white shadow-md' : 'text-stone-600 hover:bg-stone-50'}`}
              data-testid={`inspeccion-tab-${t.k}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.l}
            </button>
          );
        })}
      </div>

      {tab === 'ai' && <AiAgentTab onPackOptionsReady={handlePackOptionsReady} />}
      {tab === 'pack' && <PackTab initialOpts={packPreset} />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'req' && <RequirementsTab />}
    </div>
  );
};

export default InspeccionPage;
